import React, { useMemo, useState } from 'react';
import {
  useTable,
  useFlexLayout,
  useResizeColumns,
  useSortBy,
  usePagination,
} from 'react-table';
import { Menu, MenuItem, IconButton } from '@mui/material';
import { MoreVertical } from 'lucide-react';
import styles from './InformationRequestsTable.module.scss';
import { StringUtils, Utilities } from 'web-core';
import ExpandableMessage from './ExpandableMessage';
import BookTour from '../BookTour/BookTour';

export const InformationRequestsTable = ({ requests = [] }) => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [openBookTour, setOpenBookTour] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const handleOpenBookTour = (request) => {
    setSelectedRequest(request);
    setOpenBookTour(true);
  };

  const handleCloseBookTour = () => {
    setOpenBookTour(false);
    setSelectedRequest(null);
  };

  const data = useMemo(() => {
    return requests.map((request, index) => ({
      id: request.id,
      created_at: Utilities.formattedLocalDateTime(request.created_at),
      requester_name: StringUtils.isBlank(request.name)
        ? request.account?.name
        : request.name,
      requester_email: StringUtils.isBlank(request.email)
        ? request.account?.email
        : request.email,
      requester_phone: StringUtils.isBlank(request.phone)
        ? request.account?.phone
        : request.phone,
      community_name: request.community ? request.community.name : 'Kithward',
      community_city: request.community ? request.community.city : '-',
      community_state: request.community ? request.community.state : '-',
      request_call: request.request_call ? 'Yes' : 'No',
      account_id: request.account_id ?? 'Not Logged In',
      message: request.message || 'No message',
      request,
      index,
    }));
  }, [requests]);

  const columns = useMemo(
    () => [
      { Header: 'Created At', accessor: 'created_at' },
      {
        Header: 'Requester',
        accessor: 'requester_name',
        Cell: ({ row }) => (
          <div className={styles.requesterNameCell}>
            <span>{row.original.requester_name}</span>
            <span>{`Account ID: ${row.original.account_id ?? 'Not Logged In'}`}</span>
          </div>
        ),
      },
      { Header: 'Email', accessor: 'requester_email' },
      { Header: 'Phone', accessor: 'requester_phone' },
      {
        Header: 'Community',
        accessor: 'community_name',
        Cell: ({ row }) => (
          <div className={styles.communityCell}>
            <span
              className={styles.communityLink}
              onClick={() => handleClickCommunityProfile(row.original.request.community)}
            >
              {row.original.community_name}
            </span>
            <span>{`${row.original.community_city}, ${row.original.community_state}`}</span>
          </div>
        ),
      },
      { Header: 'Call Request', accessor: 'request_call' },
      {
        Header: 'Message',
        accessor: 'message',
        width: 300,
        Cell: ({ row }) => <ExpandableMessage message={row.original.message} />,
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        width: 80,
        Cell: ({ row }) => {
          const [anchorEl, setAnchorEl] = useState(null);
          const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
          const handleMenuClose = () => setAnchorEl(null);

          const request = row.original.request;
          const actionButtons = [];

          if (row.original.requester_phone) {
            actionButtons.push({
              label: 'Call',
              action: () => handlePhone(row.original.requester_phone),
            });
          }

          if (row.original.requester_email) {
            actionButtons.push({
              label: 'Email',
              action: () => handleEmail(row.original.requester_email),
            });
          }

          actionButtons.push({
            label: 'View Search',
            action: () => handleViewSearch(request),
          });

          actionButtons.push({
            label: 'Book a Tour',
            action: () => handleOpenBookTour(request),
          });

          return (
            <div className={styles.actionButtonContainer}>
              {/* 3-Dots Icon for Actions */}
              <IconButton
                onClick={handleMenuOpen}
                className={styles.actionIconButton}
                aria-controls='actions-menu'
                aria-haspopup='true'
              >
                <MoreVertical />
              </IconButton>

              {/* Dropdown Menu for Actions */}
              <Menu
                id='actions-menu'
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                className={styles.menu}
              >
                {actionButtons.map((action, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => {
                      action.action();
                      handleMenuClose();
                    }}
                    className={styles.menuItem}
                  >
                    {action.label}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          );
        },
      },
    ],
    []
  );

  const tableInstance = useTable(
    { columns, data, initialState: { pageSize: 10 } },
    useFlexLayout,
    useResizeColumns,
    useSortBy,
    usePagination
  );

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    tableInstance;

  const handlePhone = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const handleEmail = (email) => {
    window.location.href = `mailto:${email}?subject=Tour Request`;
  };

  const handleViewSearch = (request) => {
    window.open(
      `${process.env.REACT_APP_CONSUMER}/my-search/${request.consumer_journey_id}`,
      '_blank'
    );
  };

  const handleClickCommunityProfile = (community) => {
    if (community) {
      window.open(
        `${process.env.REACT_APP_CONSUMER}/community/${community.slug}`,
        '_blank'
      );
    }
  };

  return (
    <div className={styles.tableContainer}>
      {requests.length === 0 ? (
        <p>No information requests available.</p>
      ) : (
        <table {...getTableProps()} className={styles.infoTable}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr
                  key={row.original.id}
                  {...row.getRowProps()}
                  className={hoveredRow === row.original.index ? styles.hoveredRow : ''}
                  onMouseEnter={() => setHoveredRow(row.original.index)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  {row.cells.map((cell) => (
                    <td key={cell.column.id} {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {selectedRequest && (
        <BookTour
          open={openBookTour}
          setOpenBookTour={handleCloseBookTour}
          community={selectedRequest.community}
          request={selectedRequest}
        />
      )}
    </div>
  );
};
